import { GlobalStore } from '@roc/feature-app-core';
import {
  DocumentName,
  DocumentService,
  DocumentStore,
} from '@roc/feature-documents';
import { TodoItem, TodoStatus } from '@roc/feature-loan-details';
import { Document } from '@roc/feature-types';
import {
  capitalize,
  compareStrings,
  flatten,
  formatDate,
  GENERIC_ERROR_MESSAGE,
  LoanProcess,
} from '@roc/feature-utils';
import { FileUpload } from '@roc/ui';
import DocumentSection from 'libs/feature-documents/src/documents/documentSection';
import { action, flow, makeObservable, observable } from 'mobx';
import { LoanService } from '../../../feature-loans/src/loans/services/loanService';
import { GeneralTasksService, TaskStatus, TaskStatusDB, TaskV2Type } from '@roc/feature-general-tasks';

export class LoanTodoStore {
  private globalStore: GlobalStore;
  private loanService: LoanService;
  private documentService: DocumentService;
  private documentStore: DocumentStore;
  private generalTasksService: GeneralTasksService;

  todosByLoanProcess: Record<string, TodoItem[]> = {};
  pendingTodosCountByLoanProcess: Record<string, number> = {};
  loadingTodos: boolean;
  todosWithSubtasksByLoanProcess: Record<string, TodoItem[]> = {};
  closingConditionsDocuments: any;
  showOnlyPending: boolean;
  prioritiesFilter: string[];
  closingConditionsTodos: TodoItem[];
  loanDrawsTodosPending: Record<string, TodoItem[]> = {};


  constructor(globalStore: GlobalStore, documentStore: DocumentStore) {
    this.globalStore = globalStore;
    this.documentStore = documentStore;
    this.loanService = new LoanService();
    this.documentService = new DocumentService();
    this.generalTasksService = new GeneralTasksService();

    makeObservable(this, {
      loadingTodos: observable,
      todosByLoanProcess: observable,
      pendingTodosCountByLoanProcess: observable,
      reset: action,
      fetchTodos: flow,
      updateTodoStatus: flow,
      updateTodoStatusV2: flow,
      mapDocumentToTodo: action,
      renameDocument: flow,
      deleteDocument: flow,
      uploadSectionDocuments: flow,
      moveDocument: flow,
      todosWithSubtasksByLoanProcess: observable,
      closingConditionsDocuments: observable,
      checkIfHasClosingCondition: action,
      fetchClosingConditions: flow,
      fetchPostClosingConditionTasks: flow,
      fetchTodosForMultipleLoanProcesses: flow,
      showOnlyPending: observable,
      setShowOnlyPending: action,
      closingConditionsTodos: observable,
      fetchLoanDrawTodosPending: flow,
      loanDrawsTodosPending: observable,
      prioritiesFilter: observable,
      setPrioritiesFilter: action,
    });
  }

  reset() {
    this.todosByLoanProcess = {};
    this.pendingTodosCountByLoanProcess = {};
    this.closingConditionsDocuments = [];
    this.todosWithSubtasksByLoanProcess = {};
    this.setShowOnlyPending(false);
    this.setPrioritiesFilter([]);
    this.closingConditionsTodos = [];
    this.loanDrawsTodosPending = {};
  }

  setShowOnlyPending(showOnlyPending: boolean) {
    this.showOnlyPending = showOnlyPending;
  }

  setPrioritiesFilter(prioritiesFilter: string[]) {
    this.prioritiesFilter = prioritiesFilter;
  }

  checkIfHasClosingCondition(documentId: number) {
    if (this.closingConditionsDocuments === undefined || this.closingConditionsDocuments?.length <= 0) return false;

    const foundClosingCondition = this.closingConditionsDocuments?.findIndex(closingCondition => closingCondition.documentId === documentId);

    return foundClosingCondition !== -1;
  }

  mapTaskToTodo(task: any, loanProcess: LoanProcess = null) {

    const ccTag = task?.taskTags?.find(tag => tag.tagType === 'CC_LEVEL');
    const pcTag = task?.taskTags?.find(tag => tag.tagType === 'PC_LEVEL');

    return {
      todoId: task?.taskId,
      parentTodoId: task?.parentTaskId,
      isParentTask: task?.parentTask,
      title: task?.title,
      status: TaskStatus[task?.status],
      taskPriority: task?.priority,
      lastUpdateDate: formatDate(task?.lastUpdateDate),
      category: task?.categoryDisplayName,
      categoryDB: task?.category,
      type2: task?.type2,
      typeId2: task?.typeId2,
      taskTags: task?.taskTags,
      categoryDisplayName: task?.categoryDisplayName,
      readOnly: task?.readOnly,
      taskAssignee: task?.taskAssignee,
      isTpoRole: task?.isTpoRole,
      document: {
        ...this.documentStore.formatDocument(task?.document, loanProcess),
        // For extension line items the backend does not reliably stamp the nested
        // document with the extension it belongs to, so downstream consumers (e.g.
        // the Extension Details / Extension Agreement dialogs) would fall back to the
        // latest extension. The task's typeId2 is the id actually used to fetch this
        // todo, so it is the reliable per-row extension id — stamp it onto the document.
        ...(task?.type2 === TaskV2Type.EXTENSION_ID && task?.typeId2 != null
          ? { extensionId: task?.typeId2 }
          : {}),
        title: task?.title,
        internalTask: task?.internalTask ?? task?.document?.internalTask,
        taskAssignee: task?.document?.taskAssignee,
        closingCondition: ccTag ? {
          closingConditionId: ccTag?.taskTagId,
          title: task?.title,
          level: parseInt(ccTag?.tagValue),
          description: task?.statusUpdateReason,
        } : null,
        postClosingCondition: pcTag ? {
          level: parseInt(pcTag?.tagValue),
          title: task?.title,
          description: task?.statusUpdateReason,
        } : null,
        readOnly: task.readOnly
      }
    } as TodoItem;
  }

  mapTasksToClosingCondition(tasks: any[]): TodoItem[] {
    if (!tasks) return [];
    return tasks.map(task => this.mapTaskToTodo(task, LoanProcess.TODOS));
  }

  *fetchClosingConditions(loanId: number) {
    try {
      if (loanId) {
        const response = yield this.loanService.getTasksWithClosingConditions(loanId);
        this.closingConditionsTodos = this.mapTasksToClosingCondition(response.data.data);
      }
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *fetchPostClosingConditionTasks(loanId: number) {
    try {
      if (loanId) {
        const response = yield this.loanService.getTasksWithPostClosingConditions(loanId);
        const todos = this.mapTasksToClosingCondition(response.data.data);
        this.todosByLoanProcess[LoanProcess.TODOS] = todos;
      }
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *fetchLoanDrawTodosPending(loanId: number) {
    try {
      const response = yield this.loanService.getLoanDrawsToDosPending(loanId);

      const data = JSON.parse(response.data.data);

      const formattedData = {};

      if (data) {
        Object.keys(data).forEach(key => {

          const drawData = data[key];

          const documents = flatten(Object.values(drawData));
          const sortedDocuments = documents
            .sort((docA, docB) =>
              compareStrings(docA.title, docB.title)
            )
            .sort(
              (docA, docB) => (docA.displayOrder ?? 0) - (docB.displayOrder ?? 0)
            );

          const todos = sortedDocuments.map(doc =>
            this.mapDocumentToTodo(doc, LoanProcess.DRAWS)
          );
          formattedData[key] = todos;
        });
      }

      this.loanDrawsTodosPending = formattedData;

    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    } finally {
      this.loadingTodos = false;
    }
  }

  *fetchTodos(loanId: number, loanProcess: LoanProcess, type2?: string, typeId2?: number) {
    try {
      this.loadingTodos = true;
      const response = yield this.loanService.getLoanToDos(
        loanId,
        capitalize(loanProcess),
        type2,
        typeId2,
        this.prioritiesFilter,
        !this.showOnlyPending,
      );

      const { data } = response.data;
      const tasks = flatten(Object.values(data));
      const sortedTasks = tasks
        .sort((taskA, taskB) => compareStrings(taskA.title, taskB.title))
        .sort(
          (docA, docB) => (docA.displayOrder ?? 0) - (docB.displayOrder ?? 0)
        );

      const todos = sortedTasks.map(task => this.mapTaskToTodo(task, loanProcess));

      this.todosByLoanProcess[loanProcess] = todos;
      this.todosWithSubtasksByLoanProcess[loanProcess] = todos

      this.pendingTodosCountByLoanProcess[loanProcess] = todos.filter(todo => {
        return [TodoStatus.PENDING, TodoStatus.MORE_INFO_NEEDED, TodoStatus.BEING_REVIEWED].includes(todo.status);
      }).length;

    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    } finally {
      this.loadingTodos = false;
    }
  }

  *fetchTodosForMultipleLoanProcesses(loanId: number, loanProcessToMap: LoanProcess, loanProcessesToFetch: LoanProcess[], disableGlobalLoading = false) {
    try {
      this.loadingTodos = true;

      const response = yield this.loanService.getLoanToDosForMultipleProcesses(
        loanId,
        loanProcessesToFetch.map(loanProcess => capitalize(loanProcess)),
        !this.showOnlyPending,
        this.prioritiesFilter,
        disableGlobalLoading
      );
      const { data } = response.data;
      const tasks = flatten(Object.values(data));

      const sortedTasks = tasks.sort((a, b) => compareStrings(a.title, b.title))
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

      let sortedTasksByLoanProcess = [];
      loanProcessesToFetch.forEach(process => {
        sortedTasksByLoanProcess = sortedTasksByLoanProcess
          .concat(sortedTasks.filter(task => task.loanProcess === capitalize(process)));
      });

      const todos = sortedTasksByLoanProcess.map(task => this.mapTaskToTodo(task, loanProcessToMap));

      this.todosByLoanProcess[loanProcessToMap] = todos;
      this.todosWithSubtasksByLoanProcess[loanProcessToMap] = todos;

      this.pendingTodosCountByLoanProcess[loanProcessToMap] = todos.filter(todo => {
        return [TodoStatus.PENDING, TodoStatus.MORE_INFO_NEEDED].includes(todo.status);
      }).length;
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });

    } finally {
      this.loadingTodos = false;
    }
  }

  *updateTodoStatus( // TODO check the rest of the usages
    loanId: number,
    loanProcess: LoanProcess,
    document: Document,
    status: TaskStatusDB,
    afterActionExecuted = undefined
  ) {
    try {
      let updatedDocument;
      if (document.taskId) {
        const statusChangeResponse = yield this.generalTasksService.isStatusChangeReady(document.taskId, status);
        const statusChange = statusChangeResponse.data?.data;
        if (!statusChange.valid) {
          this.globalStore.notificationStore.showErrorNotification({
            message: statusChange.reason,
          });
          return;
        }

        const response = yield this.generalTasksService.updateTaskBasedOnLoanTask(document.taskId, {
          ...document,
          taskDetails: { ...document, status: status },
          assignees: document?.taskAssignees,
          status,
        });

        const parsedResponse = response.data.data;
        updatedDocument = parsedResponse;
      } else {
        const response = yield this.documentService.updateDocument({
          ...document,
          status,
        });
        updatedDocument = response.data.data;
      }
      const todos = this.todosByLoanProcess[loanProcess];
      const todoIndex = todos.findIndex(todo => todo.document.loanTaskId === updatedDocument.loanTaskId);

      if (todoIndex !== -1) {
        todos[todoIndex] = {
          ...todos[todoIndex],
          status: updatedDocument.status,
          lastUpdateDate: formatDate(updatedDocument.lastUpdateDate),
          document: { ...document, ...updatedDocument },
        };
      }

      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Status updated successfully.',
      });
      afterActionExecuted && afterActionExecuted();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *updateTodoStatusV2(
    document: Document,
    status: TaskStatus,
    afterActionExecuted: (() => void) | undefined = undefined
  ) {
    try {

      if (document.taskId) {
        const statusChangeResponse = yield this.generalTasksService.isStatusChangeReady(document.taskId, status);
        const statusChange = statusChangeResponse.data?.data;
        if (!statusChange.valid) {
          this.globalStore.notificationStore.showErrorNotification({
            message: statusChange.reason,
          });
          return;
        }

        if (status === TaskStatus.NOT_APPLICABLE) {
          yield this.generalTasksService.markTaskAsNotApplicable(document.taskId);
        }

        if (status === TaskStatus.ACCEPTED) {
          yield this.generalTasksService.markTaskAsCompletedV2(document.taskId);
        }

      }

      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Status updated successfully.',
      });
      afterActionExecuted && afterActionExecuted();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  mapDocumentToTodo(document: any, loanProcess: LoanProcess) {
    return {
      todoId: document.taskId,
      title: document.title,
      status: document.taskStatus || document.status,
      isParentTask: document.parentTask,
      taskPriority: document.priority,
      lastUpdateDate: formatDate(document.lastUpdateDate),
      category: document.categoryDisplayName,
      document: this.documentStore.formatDocument(document?.document, loanProcess),
    } as TodoItem;
  }

  mapLoanTaskToTodo(task: any, loanProcess: LoanProcess) {
    return {
      ...task,
      todoId: task?.document?.loanTaskId,
      title: task?.document?.taskName,
      isParentTask: task?.document?.parentTask,
      lastUpdateDate: formatDate(task?.document?.lastUpdateDate),
      category: task?.document?.sectionName,
      document: this.documentStore.formatDocument(task?.document, loanProcess),
    } as TodoItem;
  }

  *uploadSectionDocuments(
    fileUploads: FileUpload[],
    loanId: number,
    sectionId: number,
    sectionName: string,
    loanProcess: LoanProcess,
    drawId?: string,
    payoffId?: number,
    categoryName?: string,
  ) {
    try {
      const docs = this.todosByLoanProcess[loanProcess].map(t => t.document);
      const referenceDocument = docs.find(doc =>
        doc.sectionName.includes(sectionName)
      );
      const loanDocuments = this.documentStore.mapFilesToLoanDocs(
        fileUploads,
        loanId.toString(),
        sectionId,
        drawId,
        referenceDocument,
        payoffId,
        categoryName
      );

      yield this.documentStore.uploadDocumentsBatch(fileUploads, loanDocuments);
      if (loanProcess === LoanProcess.TODOS) {
        this.fetchTodosForMultipleLoanProcesses(loanId, loanProcess, [LoanProcess.UNDERWRITING, LoanProcess.CLOSING]);
      } else if (loanProcess === LoanProcess.PAYOFF) {
        this.fetchTodos(loanId, loanProcess, TaskV2Type.PAYOFF_ID, payoffId);
      } else {
        this.fetchTodos(loanId, loanProcess);
      }
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while uploading documents.',
      });
    }
  }

  *renameDocument(document: Document, newName: string) {
    try {
      yield this.documentService.editTask({
        ...document,
        title: newName,
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *deleteDocument(document: Document) {
    try {
      yield this.documentService.deleteDocument(document);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *moveDocument(
    document: Document,
    referenceDocument: Document,
    dropboxPath: string
  ) {
    try {
      yield this.documentService.editLoanDocument({
        ...document,
        customDropboxPath: dropboxPath,
        sectionId: referenceDocument.sectionId,
        borrowerId: referenceDocument.borrowerId,
        collateralId: referenceDocument.collateralId,
        borrowerEntityId: referenceDocument.borrowerEntityId,
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while moving document',
      });
    }
  }
}
